import dayjs from 'dayjs';

import type { Dayjs } from 'dayjs';
import type { UnixTimestamp } from '@vgtv/api-client';

const MAX_UNIX_TIMESTAMP = 2147483647;

function isUnixTimestamp(date?: any): date is UnixTimestamp {
  return Number.isInteger(date) && date <= MAX_UNIX_TIMESTAMP;
}

function toDayjs(date?: Dayjs | Date | number | string): Dayjs {
  if (!date) {
    return dayjs();
  }

  const input = isUnixTimestamp(date) ? date * 1000 : date;

  return dayjs(input);
}

/**
 * Returns date in format 2020-01-01
 *
 * @param {Date | number | string} date
 * @param {string} [format]
 */
function formatDate(
  date?: Dayjs | Date | string | number,
  format = 'YYYY-MM-DD'
) {
  return toDayjs(date).format(format);
}

/**
 * Returns time in format 10:20
 */
function formatTime(date?: Dayjs | Date | number | string) {
  return formatDate(date, 'HH:mm');
}

/**
 * Returns day on month in format 25. May
 */
function formatDateOfMonth(date?: Dayjs | Date | number | string) {
  const djs = toDayjs(date);
  let format = 'D. MMM';
  if (djs.get('year') !== dayjs().get('year')) {
    format = 'D. MMM YYYY';
  }

  return formatDate(djs, format);
}

function isDateInFuture(
  date?: Date | number | string,
  relativeTo?: Date | number | string
) {
  const dateObj = toDayjs(date);
  const relativeToObj = toDayjs(relativeTo);
  if (dateObj.isAfter(relativeToObj)) {
    return true;
  }

  return dateObj.isToday() && dateObj.isAfter(relativeToObj, 'h');
}

function isDateInPast(
  date?: Date | number | string,
  relativeTo?: Date | number | string
) {
  const dateObj = toDayjs(date);
  const relativeToObj = toDayjs(relativeTo);
  if (dateObj.isBefore(relativeToObj)) {
    return true;
  }

  return dateObj.isToday() && dateObj.isBefore(relativeToObj, 'h');
}

export {
  isUnixTimestamp,
  toDayjs,
  formatDate,
  formatTime,
  formatDateOfMonth,
  isDateInFuture,
  isDateInPast,
};
